import { watchLocation, isWithinRadius, type LocationCoordinates } from './location';

type GeofenceEventType = 'enter' | 'exit' | 'dwell';

export type GeofenceCategory =
  | 'itinerary'
  | 'base_camp'
  | 'landmark'
  | 'safety'
  | 'transport_hub'
  | 'custom';

export type Geofence = {
  id: string;
  name: string;
  category: GeofenceCategory;
  center: LocationCoordinates;
  radiusMeters: number;
  dwellMinutes?: number;
  tripId?: string | null;
  metadata?: Record<string, unknown>;
};

export type GeofenceEvent = {
  geofence: Geofence;
  type: GeofenceEventType;
  location: LocationCoordinates;
  occurredAt: Date;
  dwellMinutes?: number;
};

export type GeofencingHandlers = {
  onEvent: (event: GeofenceEvent) => Promise<void> | void;
  onError?: (error: Error) => void;
};

export type GeofencingOptions = {
  /**
   * Minimum time between location samples (ms)
   * Defaults to 15 seconds to balance responsiveness and battery.
   */
  sampleIntervalMs?: number;
  /**
   * Minimum distance delta (meters) before emitting a new sample.
   * Defaults to 25m to reduce noise.
   */
  distanceIntervalMeters?: number;
  /**
   * Optional global dwell threshold (minutes). Per-geofence dwellMinutes
   * overrides this value when present.
   */
  defaultDwellMinutes?: number;
};

type GeofenceState = {
  isInside: boolean;
  enteredAt?: number;
  lastNotifiedDwell?: number;
};

/**
 * Basic geofencing engine using location polling.
 * This is a fallback implementation until native Mapbox geofencing is exposed.
 */
export const startGeofenceMonitoring = (
  geofences: Geofence[],
  handlers: GeofencingHandlers,
  options: GeofencingOptions = {},
): (() => void) => {
  const sampleIntervalMs = options.sampleIntervalMs ?? 15_000;
  const distanceIntervalMeters = options.distanceIntervalMeters ?? 25;

  // Track state for each geofence
  const fenceState = new Map<string, GeofenceState>();
  geofences.forEach((fence) => fenceState.set(fence.id, { isInside: false }));

  const safeEmit = (event: GeofenceEvent) => {
    try {
      const maybePromise = handlers.onEvent(event);
      if (maybePromise && typeof (maybePromise as Promise<void>).catch === 'function') {
        (maybePromise as Promise<void>).catch((error) => {
          if (__DEV__) {
            console.error('[Geofencing] onEvent handler rejected:', error);
          }
          handlers.onError?.(error instanceof Error ? error : new Error(String(error)));
        });
      }
    } catch (error: any) {
      if (__DEV__) {
        console.error('[Geofencing] onEvent handler threw:', error);
      }
      handlers.onError?.(error instanceof Error ? error : new Error(String(error)));
    }
  };

  const handleLocation = (location: LocationCoordinates) => {
    for (const fence of geofences) {
      const state = fenceState.get(fence.id);
      if (!state) continue;

      const inside = isWithinRadius(location, fence.center, fence.radiusMeters);
      const now = Date.now();

      // Enter event
      if (inside && !state.isInside) {
        state.isInside = true;
        state.enteredAt = now;
        state.lastNotifiedDwell = undefined;
        safeEmit({
          geofence: fence,
          type: 'enter',
          location,
          occurredAt: new Date(now),
        });
        continue;
      }

      // Exit event
      if (!inside && state.isInside) {
        state.isInside = false;
        state.enteredAt = undefined;
        state.lastNotifiedDwell = undefined;
        safeEmit({
          geofence: fence,
          type: 'exit',
          location,
          occurredAt: new Date(now),
        });
        continue;
      }

      // Dwell event
      const dwellMinutes = fence.dwellMinutes ?? options.defaultDwellMinutes;
      if (inside && state.isInside && dwellMinutes && state.enteredAt) {
        const minutesInside = (now - state.enteredAt) / 1000 / 60;
        const alreadyNotified =
          state.lastNotifiedDwell && state.lastNotifiedDwell >= dwellMinutes;

        if (!alreadyNotified && minutesInside >= dwellMinutes) {
          state.lastNotifiedDwell = minutesInside;
          safeEmit({
            geofence: fence,
            type: 'dwell',
            location,
            occurredAt: new Date(now),
            dwellMinutes: minutesInside,
          });
        }
      }
    }
  };

  // Start polling location via expo-location helper
  const stopLocation = watchLocation(handleLocation, {
    timeInterval: sampleIntervalMs,
    distanceInterval: distanceIntervalMeters,
  });

  return () => {
    try {
      stopLocation();
    } catch (error) {
      if (__DEV__) {
        console.warn('[Geofencing] Error stopping monitoring:', error);
      }
    }
  };
};

/**
 * Utility to prepare point-radius geofences from arbitrary coordinates.
 */
export const buildPointGeofence = (params: {
  id: string;
  name: string;
  category: GeofenceCategory;
  coordinates: LocationCoordinates;
  radiusMeters?: number;
  dwellMinutes?: number;
  tripId?: string | null;
  metadata?: Record<string, unknown>;
}): Geofence => {
  return {
    id: params.id,
    name: params.name,
    category: params.category,
    center: params.coordinates,
    radiusMeters: params.radiusMeters ?? 120, // default ~1-2 city blocks
    dwellMinutes: params.dwellMinutes,
    tripId: params.tripId ?? null,
    metadata: params.metadata,
  };
};

/**
 * Helper to determine if Mapbox-native geofencing is available.
 * Placeholder for future native integration.
 */
export const isNativeGeofencingAvailable = (): boolean => {
  // As of @rnmapbox/maps 10.x the native geofencing API is not exposed.
  return false;
};
